import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronRight, Star, TrendingUp, Clock, FileText,
  Users, DollarSign, Building2, Building, Shield, Activity, Zap
} from "lucide-react";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── FundingProgressBar ─── */
export function FundingProgressBar({ raised = 0, target = 1, label, className = "" }) {
  const pct = Math.min(100, target > 0 ? (raised / target) * 100 : 0);
  const color = pct >= 100 ? "bg-emerald-500" : pct >= 75 ? "bg-emerald-400" : pct >= 50 ? "bg-amber-400" : "bg-blue-400";
  const state = pct >= 100 ? "Fully Funded" : pct >= 75 ? "Nearly Funded" : pct >= 50 ? "Progressing" : "Open";
  return (
    <div className={className} data-testid="funding-progress-bar">
      {label && <div className="flex justify-between text-[10px] mb-1"><span className="text-[#888]">{label}</span><span className="font-bold text-[#333]">{state}</span></div>}
      <div className="h-2.5 rounded-full bg-[#e8e0d0] overflow-hidden">
        <motion.div className={`h-full rounded-full ${color}`} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
      </div>
      <div className="flex justify-between text-[9px] text-[#999] mt-1">
        <span>{fmtUSD(raised)} raised</span>
        <span>{pct.toFixed(0)}% of {fmtUSD(target)}</span>
      </div>
    </div>
  );
}

/* ─── AllocationStatus ─── */
export function AllocationStatus({ status = "draft" }) {
  const states = {
    draft: { bg: "bg-gray-100", text: "text-gray-600", label: "Draft" },
    approved_for_marketplace: { bg: "bg-blue-100", text: "text-blue-600", label: "Approved" },
    open_for_commitments: { bg: "bg-emerald-100", text: "text-emerald-600", label: "Open" },
    nearly_funded: { bg: "bg-amber-100", text: "text-amber-600", label: "Nearly Funded" },
    fully_funded: { bg: "bg-green-100", text: "text-green-700", label: "Fully Funded" },
    closed: { bg: "bg-red-100", text: "text-red-600", label: "Closed" },
  };
  const s = states[status] || states.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${s.bg} ${s.text}`} data-testid="allocation-status">{s.label}</span>;
}

/* ─── ReputationBadge ─── */
export function ReputationBadge({ score = 0, size = "sm" }) {
  const color = score >= 80 ? "text-emerald-600 border-emerald-200 bg-emerald-50" : score >= 50 ? "text-amber-600 border-amber-200 bg-amber-50" : "text-gray-500 border-gray-200 bg-gray-50";
  const sizeClass = size === "lg" ? "h-12 w-12 text-lg" : "h-7 w-7 text-[10px]";
  return (
    <div className={`${sizeClass} rounded-full border-2 ${color} flex items-center justify-center font-black`} data-testid="reputation-badge" title={`Reputation: ${score}/100`}>
      {score}
    </div>
  );
}

/* ─── ReputationDetailCard ─── */
export function ReputationDetailCard({ data, entityType, className = "" }) {
  if (!data || !data.score) return (
    <div className={`rounded-xl border border-[#e8e0d0] bg-white p-6 text-center ${className}`} data-testid="reputation-detail-card">
      <div className="h-14 w-14 rounded-full border-2 border-gray-200 bg-gray-50 flex items-center justify-center mx-auto mb-3">
        <span className="text-xl font-black text-gray-400">—</span>
      </div>
      <div className="text-sm font-bold text-[#333] mb-1">No Reputation Data Yet</div>
      <div className="text-xs text-[#888]">Reputation scores are built through platform activity. As this {entityType.toLowerCase()} completes more transactions, their score will appear here.</div>
    </div>
  );
  const { score, factors = {}, breakdown = {} } = data;
  const color = score >= 80 ? "emerald" : score >= 50 ? "amber" : "gray";
  const label = score >= 80 ? "Excellent" : score >= 60 ? "Good" : score >= 40 ? "Building" : "New";

  const factorEntries = Object.entries(factors).filter(([, v]) => v > 0 || true);

  return (
    <div className={`rounded-xl border border-[#e8e0d0] bg-white p-4 ${className}`} data-testid="reputation-detail-card">
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-14 w-14 rounded-full border-3 border-${color}-300 bg-${color}-50 flex items-center justify-center`}>
          <span className={`text-xl font-black text-${color}-600`}>{score}</span>
        </div>
        <div>
          <div className="text-xs font-bold text-[#222]">Capital Reputation</div>
          <div className={`text-[10px] font-semibold text-${color}-600`}>{label}</div>
          <div className="text-[10px] text-[#999]">{entityType} score</div>
        </div>
      </div>
      {/* Score Bar */}
      <div className="h-2 rounded-full bg-[#e8e0d0] overflow-hidden mb-3">
        <div className={`h-full rounded-full bg-${color}-500 transition-all duration-700`} style={{ width: `${score}%` }} />
      </div>
      {/* Factor Breakdown */}
      <div className="space-y-1.5">
        {factorEntries.map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-[10px] text-[#888] capitalize">{key.replace(/_/g, ' ')}</span>
            <div className="flex items-center gap-2">
              <div className="w-16 h-1.5 rounded-full bg-[#e8e0d0] overflow-hidden">
                <div className={`h-full rounded-full ${value >= 20 ? "bg-emerald-400" : value >= 10 ? "bg-amber-400" : "bg-gray-300"}`} style={{ width: `${Math.min(100, value * 3.3)}%` }} />
              </div>
              <span className="text-[10px] font-bold text-[#555] w-5 text-right">{value}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Key Stats */}
      {Object.keys(breakdown).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#e8e0d0] grid grid-cols-2 gap-2">
          {Object.entries(breakdown).slice(0, 4).map(([key, value]) => (
            <div key={key} className="text-center">
              <div className="text-xs font-bold text-[#333]">{value}</div>
              <div className="text-[9px] text-[#999] capitalize">{key.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── MetricCard ─── */
export function MetricCard({ icon: Icon = Activity, label, value, accent = false, className = "" }) {
  return (
    <div className={`rounded-xl border border-[#e8e0d0] bg-white p-3 text-center ${className}`} data-testid={`metric-card-${label?.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className={`h-4 w-4 mx-auto mb-1 ${accent ? "text-emerald-600" : "text-[#999]"}`} />
      <div className={`text-sm font-bold ${accent ? "text-emerald-700" : "text-[#222]"}`}>{value}</div>
      <div className="text-[9px] text-[#999] uppercase tracking-wider">{label}</div>
    </div>
  );
}

/* ─── StatusCard ─── */
export function StatusCard({ title, status, children, className = "" }) {
  const statusColors = {
    active: "border-l-emerald-500", funded: "border-l-green-500", pending: "border-l-amber-500",
    closed: "border-l-red-500", draft: "border-l-gray-400", open: "border-l-blue-500",
  };
  return (
    <div className={`rounded-xl border border-[#e8e0d0] bg-white p-4 border-l-4 ${statusColors[status] || "border-l-gray-300"} ${className}`} data-testid="status-card">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-[#222]">{title}</h4>
        <AllocationStatus status={status} />
      </div>
      {children}
    </div>
  );
}

/* ─── EntityCard ─── */
export function EntityCard({ type, id, title, subtitle, status, score, onClick, className = "" }) {
  const typeIcons = { deal: Building2, borrower: Users, lender: Users, investor: DollarSign, opportunity: TrendingUp, document: FileText, commitment: DollarSign };
  const Icon = typeIcons[type] || Activity;
  const typeColors = { deal: "bg-emerald-100 text-emerald-700", borrower: "bg-blue-100 text-blue-700", lender: "bg-purple-100 text-purple-700", investor: "bg-amber-100 text-amber-700", opportunity: "bg-teal-100 text-teal-700" };
  return (
    <div className={`rounded-xl border border-[#e8e0d0] bg-white p-3 hover:shadow-sm transition-shadow cursor-pointer ${className}`} onClick={() => onClick?.(type, id)} data-testid={`entity-card-${type}-${id}`}>
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-xs font-bold ${typeColors[type] || "bg-gray-100 text-gray-700"}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-[#222] truncate">{title}</span>
            {score > 0 && <ReputationBadge score={score} />}
          </div>
          {subtitle && <p className="text-[10px] text-[#999] truncate">{subtitle}</p>}
        </div>
        {status && <AllocationStatus status={status} />}
        <ChevronRight className="h-3.5 w-3.5 text-[#ccc] shrink-0" />
      </div>
    </div>
  );
}

/* ─── TimelineCard ─── */
export function TimelineCard({ events = [] }) {
  if (!events.length) return <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No timeline events.</div>;
  return (
    <div className="space-y-0" data-testid="timeline-card">
      {events.map((e, i) => (
        <div key={i} className="flex items-start gap-3 py-2.5 relative">
          {i < events.length - 1 && <div className="absolute left-[7px] top-[20px] bottom-0 w-px bg-[#e8e0d0]" />}
          <div className={`mt-1 h-3.5 w-3.5 rounded-full shrink-0 border-2 ${e.type === "commitment" ? "border-emerald-500 bg-emerald-100" : e.type === "term_sheet" ? "border-blue-500 bg-blue-100" : "border-[#ccc] bg-white"}`} />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#333]">{e.description || e.event_type || e.event}</p>
            <p className="text-[10px] text-[#bbb]">{e.timestamp ? new Date(e.timestamp).toLocaleString() : e.created_at ? new Date(e.created_at).toLocaleString() : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── DocumentCard ─── */
export function DocumentCard({ doc, onClick }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const downloadUrl = `${API}/api/documents/download/${doc.doc_id}?auth=${localStorage.getItem("ic_token")}`;
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between hover:shadow-sm transition-shadow" data-testid={`document-card-${doc.doc_id}`}>
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => onClick?.("document", doc.doc_id)}>
        <FileText className="h-4 w-4 text-emerald-600" />
        <div>
          <p className="text-xs font-medium text-[#333]">{doc.original_filename || doc.name}</p>
          <div className="flex gap-2 text-[10px] text-[#999] items-center">
            {doc.doc_type && <span>{doc.doc_type}</span>}
            {doc.visibility && <span className="px-1 py-0.5 rounded bg-blue-50 text-blue-600">{doc.visibility}</span>}
            {doc.carried_from_profile && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 600, background: "rgba(64,145,108,0.1)", color: "#1B4332", border: "1px solid rgba(64,145,108,0.2)" }}
                data-testid={`carried-badge-${doc.doc_id}`}>
                From Profile
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[#bbb]">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ""}</span>
        <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-cream border border-cream-warm text-[#555] hover:text-emerald-700 hover:border-emerald-300 transition-colors" data-testid={`download-doc-${doc.doc_id}`}>View</a>
      </div>
    </div>
  );
}

/* ─── RelatedEntitiesPanel ─── */
export function RelatedEntitiesPanel({ entities = [], onNavigate }) {
  if (!entities.length) return null;
  const grouped = {};
  entities.forEach(e => { (grouped[e.type] = grouped[e.type] || []).push(e); });
  const typeIcons = { deal: DollarSign, borrower: Users, lender: Building, investor: TrendingUp, opportunity: FileText, document: FileText, commitment: DollarSign };
  const typeColors = { deal: "bg-blue-100 text-blue-600", borrower: "bg-amber-100 text-amber-600", lender: "bg-purple-100 text-purple-600", investor: "bg-emerald-100 text-emerald-600", opportunity: "bg-gold/20 text-gold-dark", document: "bg-slate-100 text-slate-600", commitment: "bg-green-100 text-green-600" };
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="related-entities-panel">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3 flex items-center gap-1.5">
        <span className="h-1 w-1 rounded-full bg-emerald-400" /> Related Entities ({entities.length})
      </h3>
      {Object.entries(grouped).map(([type, items]) => {
        const TypeIcon = typeIcons[type] || Activity;
        const colorClass = typeColors[type] || "bg-gray-100 text-gray-600";
        return (
          <div key={type} className="mb-3 last:mb-0">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className={`h-4 w-4 rounded flex items-center justify-center ${colorClass}`}>
                <TypeIcon className="h-2.5 w-2.5" />
              </div>
              <span className="text-[9px] font-bold uppercase text-[#bbb]">{type}s ({items.length})</span>
            </div>
            <div className="space-y-1 pl-2 border-l-2 border-[#e8e0d0] ml-2">
              {items.slice(0, 5).map((e, i) => (
                <EntityCard key={`${e.type}-${e.id}-${i}`} type={e.type} id={e.id} title={e.title} subtitle={e.subtitle} onClick={onNavigate} className="!p-2" />
              ))}
              {items.length > 5 && <p className="text-[10px] text-[#999] pl-2">+{items.length - 5} more</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── QuickActionsDrawer ─── */
export function QuickActionsDrawer({ actions = [], isOpen, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div className="fixed inset-0 bg-black/20 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 border-l border-[#e8e0d0] overflow-y-auto"
            initial={{ x: 320 }} animate={{ x: 0 }} exit={{ x: 320 }} transition={{ type: "spring", damping: 25 }}>
            <div className="sticky top-0 bg-white border-b border-[#e8e0d0] p-4 flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#222]">Quick Actions</h3>
              <button onClick={onClose} className="p-1 rounded hover:bg-[#faf7f2]"><X className="h-4 w-4 text-[#888]" /></button>
            </div>
            <div className="p-4 space-y-2" data-testid="quick-actions-drawer">
              {actions.map((action, i) => (
                <button key={i} onClick={() => { action.onClick?.(); onClose(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#e8e0d0] hover:bg-[#faf7f2] hover:border-emerald-200 transition-all text-left"
                  data-testid={`quick-action-${action.id || i}`}>
                  {action.icon && <action.icon className="h-4 w-4 text-emerald-600 shrink-0" />}
                  <div>
                    <div className="text-xs font-semibold text-[#222]">{action.label}</div>
                    {action.description && <div className="text-[10px] text-[#999]">{action.description}</div>}
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ─── ActivityFeed ─── */
export function ActivityFeed({ events = [], maxItems = 10 }) {
  const typeIcons = { commitment: DollarSign, term_sheet: FileText, timeline: Clock, stage_change: TrendingUp, document: FileText };
  const typeColors = { commitment: "text-emerald-600 bg-emerald-50", term_sheet: "text-blue-600 bg-blue-50", timeline: "text-[#888] bg-[#faf7f2]" };
  if (!events.length) return <div className="rounded-xl border border-[#e8e0d0] bg-white p-6 text-center text-xs text-[#888]">No recent activity.</div>;
  return (
    <div className="space-y-1" data-testid="activity-feed">
      {events.slice(0, maxItems).map((e, i) => {
        const Icon = typeIcons[e.type] || Activity;
        const colorClass = typeColors[e.type] || "text-[#888] bg-[#faf7f2]";
        return (
          <div key={i} className="flex items-start gap-2.5 py-2">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
              <Icon className="h-3 w-3" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#333]">{e.description}</p>
              <p className="text-[10px] text-[#bbb]">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Entity360Shell ─── */
export function Entity360Shell({ type, title, subtitle, status, badges = [], kpis = [], tabs, activeTab, onTabChange, children, onBack, breadcrumbs, onNavigate, quickActions = [], reputation, relatedEntities = [] }) {
  const [showActions, setShowActions] = useState(false);
  const typeColors = { deal: "text-emerald-700", borrower: "text-blue-700", lender: "text-purple-700", investor: "text-amber-700", opportunity: "text-teal-700", fund: "text-indigo-700", exception: "text-red-700", distribution: "text-green-700", document: "text-gray-700", commitment: "text-cyan-700" };
  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid={`${type}-360-shell`}>
      {breadcrumbs?.length > 0 && (
        <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-4">
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="h-2.5 w-2.5" />}
              <button className="hover:text-emerald-600 transition-colors" onClick={() => onNavigate?.(b.type, b.id)}>{b.label}</button>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          {reputation > 0 && <ReputationBadge score={reputation} size="lg" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={`text-lg sm:text-xl font-black ${typeColors[type] || "text-[#222]"} truncate`}>{title}</h1>
              {status && <AllocationStatus status={status} />}
              {badges.map((b, i) => <span key={i} className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${b.className || "bg-gray-100 text-gray-600"}`}>{b.label}</span>)}
            </div>
            {subtitle && <p className="text-xs text-[#888] mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {quickActions.length > 0 && (
            <button onClick={() => setShowActions(true)} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1.5" data-testid="quick-actions-trigger">
              <Zap className="h-3.5 w-3.5" /> Actions
            </button>
          )}
          {onBack && <button onClick={onBack} className="px-3 py-1.5 rounded-lg border border-[#e8e0d0] text-xs text-[#666] hover:bg-[#faf7f2]">Back</button>}
        </div>
      </div>
      {kpis.length > 0 && (
        <div className={`grid gap-3 mb-6 grid-cols-2 sm:grid-cols-3`} style={{ gridTemplateColumns: undefined }} >
          {kpis.map((k, i) => <MetricCard key={i} {...k} />)}
        </div>
      )}
      {tabs && (
        <div className="flex gap-1 overflow-x-auto mb-6 border-b border-[#e8e0d0]">
          {tabs.map(t => (
            <button key={t.key} onClick={() => onTabChange(t.key)}
              className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${activeTab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-[#888] hover:text-[#555]"}`}
              data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div className={`grid gap-6 ${relatedEntities.length > 0 ? "lg:grid-cols-[1fr_280px]" : ""}`}>
        <div>{children}</div>
        {relatedEntities.length > 0 && <RelatedEntitiesPanel entities={relatedEntities} onNavigate={onNavigate} />}
      </div>
      <QuickActionsDrawer actions={quickActions} isOpen={showActions} onClose={() => setShowActions(false)} />
    </div>
  );
}
